import React from 'react';
import { useDispatch } from 'react-redux';
import { FormControl, FormControlLabel, Grid, IconButton, Radio, RadioGroup, Tooltip, Typography } from '@mui/material';

import useSettings from 'app/main/components/hooks/useSettings';
import useUserData from 'app/main/components/hooks/useUserData';
import { alertProgressMsg, alertSuccessMsg, executeStored } from 'app/main/components/Utils';
import Icon from 'app/main/components/Icon';

import DetailsSectionWrapper from '../components/DetailsSectionWrapper';
import { showVisibilityGroupsAddDialog } from '../components/VisibilityGroupsAddDialog';
import { showVisibilityUsersAddDialog } from '../components/VisibilityUsersAddDialog';
import ViewList from '../../view/list/ViewList';

import GroupsVisibilityListItem from 'app/main/modules/groups/GroupsVisibilityListItem';
import UsersVisibilityListItem from 'app/main/modules/users/UsersVisibilityListItem';

interface DetailsVisibilityTabProps {
	module: string;
	model: any;
}

function RadioOption({ children }: { children: React.ReactNode }) {
	const { t } = useSettings();

	return (
		<FormControlLabel
			className="mr-0 pb-8"
			sx={{
				'&:last-child': {
					paddingBottom: 0
				}
			}}
			value={children}
			control={<Radio />}
			label={
				<>
					<Typography variant="subtitle2">{t(`${children}_LEVEL_TITLE`)}</Typography>
					<Typography>{t(`${children}_LEVEL_DESCRIPTION`)}</Typography>
				</>
			}
		/>
	);
}

export default function DetailsVisibilityTab({ module, model }: DetailsVisibilityTabProps) {
	const dispatch = useDispatch();
	const { settings, t } = useSettings(module);
	const userData = useUserData();

	const idField = `${settings.modu_short}_uid`;
	const modifiedUserField = `${settings.modu_short}_user_modified_uid`;
	const visibilityField = `${settings.modu_short}_visibility`;

	const [visibilityLevel, setVisibilityLevel] = React.useState(model[visibilityField] || 'PUBLIC');

	const [refresh, setRefresh] = React.useState(true);
	const updateRefresh = () => setRefresh(value => !value);

	function handleChangeVisibility(_event, value) {
		dispatch(alertProgressMsg(t));

		executeStored({
			action: `st_${settings.modu_title}_update`,
			dispatch,
			data: {
				[idField]: model[idField],
				[modifiedUserField]: userData.user_uid,
				[visibilityField]: value
			},
			successHandler: () => {
				setVisibilityLevel(value);
				dispatch(alertSuccessMsg(t));
				updateRefresh();
			}
		});
	}

	function addGroup() {
		dispatch(showVisibilityGroupsAddDialog({ module, uid: model[idField], updateRefresh }));
	}

	function addUser() {
		dispatch(showVisibilityUsersAddDialog({ module, uid: model[idField], updateRefresh }));
	}

	return (
		<Grid container spacing={2}>
			<Grid size={{ md: 4, xs: 12 }}>
				<DetailsSectionWrapper
					avatar={<Icon icon="eye" />}
					title={t('DETAILS_VISIBILITY_LEVEL_SECTION')}
					subheader={t('DETAILS_VISIBILITY_LEVEL_SUBHEADER')}
					disablePadding
				>
					<FormControl className="w-full p-16 pt-0">
						<RadioGroup name={visibilityField} value={visibilityLevel} onChange={handleChangeVisibility}>
							<RadioOption>PUBLIC</RadioOption>
							<RadioOption>INTERNAL</RadioOption>
							<RadioOption>GROUP</RadioOption>
							<RadioOption>USERS</RadioOption>
						</RadioGroup>
					</FormControl>
				</DetailsSectionWrapper>
			</Grid>
			<Grid size={{ md: 8, xs: 12 }}>
				<Grid container spacing={2}>
					{visibilityLevel === 'GROUP' && (
						<Grid size={12}>
							<DetailsSectionWrapper
								avatar={<Icon icon="account-group" />}
								title={t('DETAILS_VISIBILITY_GROUPS_SECTION')}
								subheader={t('DETAILS_VISIBILITY_GROUPS_SUBHEADER')}
								headerAction={
									<Tooltip title={t('DIALOG_GROUP_ADD_TITLE')} arrow>
										<IconButton onClick={addGroup}>
											<Icon icon="plus-circle" />
										</IconButton>
									</Tooltip>
								}
								disablePadding
							>
								<ViewList
									module={`${module}_groups`}
									itemLayout={GroupsVisibilityListItem}
									refresh={refresh}
									updateRefresh={updateRefresh}
									defaultFilters={[
										{
											filt_field: idField,
											filt_clause: 'equals',
											filt_value: model[idField]
										}
									]}
									showFilters={false}
									showExportButton={false}
								/>
							</DetailsSectionWrapper>
						</Grid>
					)}
					<Grid size={12}>
						<DetailsSectionWrapper
							avatar={<Icon icon="account-circle" />}
							title={t('DETAILS_VISIBILITY_USERS_SECTION')}
							subheader={t('DETAILS_VISIBILITY_USERS_SUBHEADER')}
							headerAction={
								visibilityLevel === 'USERS' && (
									<Tooltip title={t('DIALOG_USER_ADD_TITLE')} arrow>
										<IconButton onClick={addUser}>
											<Icon icon="plus-circle" />
										</IconButton>
									</Tooltip>
								)
							}
							disablePadding
						>
							<ViewList
								module={`${module}_users`}
								itemLayout={UsersVisibilityListItem}
								refresh={refresh}
								updateRefresh={updateRefresh}
								defaultFilters={[
									{
										filt_field: 'user_enabled',
										filt_clause: 'not_equal',
										filt_value: 1
									},
									{
										filt_field: idField,
										filt_clause: 'equals',
										filt_value: model[idField]
									}
								]}
							/>
						</DetailsSectionWrapper>
					</Grid>
				</Grid>
			</Grid>
		</Grid>
	);
}
